/**
 * SettingsRow — one editable line in the device-settings inspector.
 *
 * Default state shows label + current value + a pencil icon that
 * reveals on row hover. Click the row or the pencil to enter the
 * appropriate per-type editor; commit with Enter or click-outside,
 * cancel with Esc.
 *
 * State machine:    idle ──click──▶ editing ──save──▶ pending
 *                     ▲                                  │
 *                     │                ┌─ success ───────┤
 *                     │                ▼                 │
 *                     ├─ idle ◀────────                  ▼
 *                     │                            errored
 *                     └─ click retry ─────────────────────┘
 *
 * Per-type editors live inline at the bottom of the file. Each takes
 * { initial, descriptor, onCommit, onCancel } and emits the new value
 * through onCommit (or onCancel without a value).
 */
import { useEffect, useRef, useState } from 'react';
import {
  SETTINGS_ERROR_LABEL,
  useSetDeviceSettings,
  type SettingDescriptor,
  type SettingsCategory,
} from '../api/hooks';

// ─── Field-value display helpers ────────────────────────────────────

function displayValue(
  value: unknown,
  descriptor: SettingDescriptor,
): string {
  // Write-only fields (PINs) never reveal the value, even when the
  // shadow hasn't reported one yet — the bullets signal "this field
  // exists, you can set it" while preserving the never-display-secret
  // invariant.
  if (descriptor.write_only) return '••••';
  if (value === null || value === undefined) return '—';
  if (descriptor.type === 'bool') return value ? 'on' : 'off';
  if (descriptor.type === 'enum') {
    const opt = descriptor.enum?.find((e) => e.value === value);
    return opt ? opt.label : String(value);
  }
  if (descriptor.type === 'text[]') {
    if (!Array.isArray(value)) return '—';
    return value.length === 0 ? '—' : value.join(', ');
  }
  return String(value);
}

// Pretty label for the row — "vol" → "Volume", "startup_vol" → "Startup volume"
function humaniseKey(k: string): string {
  return k
    .replace(/_/g, ' ')
    .replace(/(^|\s)\S/g, (m) => m.toUpperCase());
}

// ─── Per-type editors ────────────────────────────────────────────────

interface EditorProps<T> {
  initial: T;
  descriptor: SettingDescriptor;
  onCommit: (value: T) => void;
  onCancel: () => void;
}

function IntEditor({
  initial,
  descriptor,
  onCommit,
  onCancel,
}: EditorProps<number>) {
  const [v, setV] = useState<string>(String(initial));
  const ref = useRef<HTMLInputElement | null>(null);
  useEffect(() => ref.current?.select(), []);
  const min = descriptor.range?.min;
  const max = descriptor.range?.max;
  const step = descriptor.range?.step ?? 1;
  return (
    <input
      ref={ref}
      type="number"
      value={v}
      min={min}
      max={max}
      step={step}
      onChange={(e) => setV(e.target.value)}
      onBlur={() => {
        const n = Number(v);
        if (Number.isFinite(n)) onCommit(n);
        else onCancel();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onCancel();
        if (e.key === 'Enter') {
          const n = Number(v);
          if (Number.isFinite(n)) onCommit(n);
        }
      }}
      placeholder={
        min !== undefined && max !== undefined
          ? `${initial} — ${min} to ${max}`
          : undefined
      }
      className="px-2 py-1 text-sm font-mono tabular-nums border border-border rounded w-24 text-right focus:outline-none focus:border-accent"
    />
  );
}

function BoolEditor({
  initial,
  onCommit,
  onCancel,
}: EditorProps<boolean>) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onCancel();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onCancel]);
  return (
    <div ref={containerRef} className="inline-flex rounded-md overflow-hidden border border-border">
      {[
        { v: false, label: 'off' },
        { v: true, label: 'on' },
      ].map((o) => (
        <button
          key={String(o.v)}
          type="button"
          onClick={() => onCommit(o.v)}
          className={`px-3 py-1 text-xs ${
            initial === o.v
              ? 'bg-accent text-white'
              : 'bg-white text-muted hover:text-fg'
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function EnumEditor({
  initial,
  descriptor,
  onCommit,
  onCancel,
}: EditorProps<number | string>) {
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onCancel();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onCancel]);
  return (
    <div className="inline-flex flex-wrap rounded-md overflow-hidden border border-border max-w-full">
      {descriptor.enum?.map((o) => (
        <button
          key={String(o.value)}
          type="button"
          onClick={() => onCommit(o.value)}
          className={`px-3 py-1 text-xs ${
            initial === o.value
              ? 'bg-accent text-white'
              : 'bg-white text-muted hover:text-fg'
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function TextEditor({
  initial,
  onCommit,
  onCancel,
}: EditorProps<string>) {
  const [v, setV] = useState(initial);
  const ref = useRef<HTMLInputElement | null>(null);
  useEffect(() => ref.current?.select(), []);
  return (
    <input
      ref={ref}
      type="text"
      value={v}
      onChange={(e) => setV(e.target.value)}
      onBlur={() => onCommit(v)}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onCancel();
        if (e.key === 'Enter') onCommit(v);
      }}
      className="px-2 py-1 text-sm border border-border rounded w-64 focus:outline-none focus:border-accent"
    />
  );
}

function TextListEditor({
  initial,
  onCommit,
  onCancel,
}: EditorProps<string[]>) {
  const [items, setItems] = useState<string[]>([...initial]);
  const [draft, setDraft] = useState('');
  const inputRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => inputRef.current?.focus(), []);
  return (
    <div className="inline-flex flex-wrap items-center gap-1 max-w-md">
      {items.map((it, i) => (
        <span
          key={`${it}-${i}`}
          className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-mono bg-soft border border-border rounded"
        >
          {it}
          <button
            type="button"
            onClick={() => setItems(items.filter((_, j) => j !== i))}
            className="text-muted hover:text-fg leading-none"
            aria-label={`Remove ${it}`}
          >
            ×
          </button>
        </span>
      ))}
      <input
        ref={inputRef}
        type="text"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        placeholder="+ add"
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            const v = draft.trim();
            if (v && !items.includes(v)) setItems([...items, v]);
            setDraft('');
            e.preventDefault();
          } else if (e.key === 'Backspace' && draft === '' && items.length) {
            setItems(items.slice(0, -1));
          } else if (e.key === 'Escape') {
            onCancel();
          }
        }}
        onBlur={() => {
          // Flush the in-flight draft, then commit the list as the
          // user left it. Click-outside commits, matching other editors.
          const next =
            draft.trim() && !items.includes(draft.trim())
              ? [...items, draft.trim()]
              : items;
          onCommit(next);
        }}
        className="px-2 py-0.5 text-xs font-mono border border-border rounded w-20 focus:outline-none focus:border-accent"
      />
    </div>
  );
}

function WriteOnlyEditor({
  onCommit,
  onCancel,
}: EditorProps<string>) {
  const [v, setV] = useState('');
  const ref = useRef<HTMLInputElement | null>(null);
  useEffect(() => ref.current?.focus(), []);
  return (
    <span className="inline-flex items-center gap-2">
      <input
        ref={ref}
        type="password"
        value={v}
        onChange={(e) => setV(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') onCancel();
          if (e.key === 'Enter' && v) onCommit(v);
        }}
        placeholder="New value"
        className="px-2 py-1 text-sm border border-border rounded w-40 focus:outline-none focus:border-accent"
      />
      <button
        type="button"
        onClick={() => v && onCommit(v)}
        disabled={!v}
        className="px-2 py-1 rounded-md text-xs font-medium bg-accent text-white disabled:opacity-30 disabled:cursor-not-allowed"
      >
        Set
      </button>
      <button
        type="button"
        onClick={onCancel}
        className="px-2 py-1 rounded-md text-xs text-muted hover:text-fg"
      >
        Cancel
      </button>
    </span>
  );
}

// ─── SettingsRow ─────────────────────────────────────────────────────

type RowMode = 'idle' | 'editing' | 'pending' | 'errored';

/** Shadow values are primitives or string arrays — structural compare. */
function sameValue(a: unknown, b: unknown): boolean {
  return JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
}

/** How long a write may sit unapplied before we say so out loud. */
const PENDING_PATIENCE_MS = 120_000;

export function SettingsRow({
  thing,
  category,
  descriptor,
  reportedValue,
  desiredValue,
  rejection,
}: {
  thing: string;
  category: SettingsCategory;
  descriptor: SettingDescriptor;
  reportedValue: unknown;
  /** The shadow's desired half. Differs from reported => not applied yet. */
  desiredValue?: unknown;
  /** Set when the device refused this key, from its settings_rejected event. */
  rejection?: { code: number; ts: string };
}) {
  const [mode, setMode] = useState<RowMode>('idle');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [retryValue, setRetryValue] = useState<unknown>(null);
  const [pendingSince, setPendingSince] = useState<number | null>(null);
  const mutation = useSetDeviceSettings();
  const rowRef = useRef<HTMLDivElement | null>(null);

  // The shadow is the source of truth for "has this landed?": desired and
  // reported disagreeing means the device has not applied it. Deriving it
  // rather than trusting the mutation's response is what makes a page reload
  // mid-write — or a write someone else made — render correctly.
  const shadowPending =
    desiredValue !== undefined && !sameValue(desiredValue, reportedValue);

  const isReadOnly = descriptor.read_only;
  const isWriteOnly = descriptor.write_only;
  const label = humaniseKey(descriptor.key);

  const beginEdit = (presetValue?: unknown) => {
    if (isReadOnly) return;
    setMode('editing');
    setErrorMsg(null);
    setRetryValue(presetValue ?? null);
  };

  const commit = (newValue: unknown) => {
    setMode('pending');
    setRetryValue(newValue);
    setPendingSince(Date.now());
    mutation.mutate(
      { thing, category, delta: { [descriptor.key]: newValue } },
      {
        // 202 means accepted and written to desired — NOT applied. The row
        // stays pending until the device reports the value back, or until a
        // settings_rejected event says it refused.
        onSuccess: () => setErrorMsg(null),
        onError: (err: Error) => {
          // This path is the API refusing the write (validation, 403, the
          // device not being visible) — the device never heard about it.
          setErrorMsg(err.message);
          setMode('errored');
          setPendingSince(null);
        },
      },
    );
  };

  const cancel = () => {
    setMode('idle');
    setErrorMsg(null);
    setPendingSince(null);
  };

  // Converged: the device reports what we asked for.
  useEffect(() => {
    if (mode === 'pending' && retryValue !== null && sameValue(reportedValue, retryValue)) {
      setMode('idle');
      setPendingSince(null);
    }
  }, [mode, retryValue, reportedValue]);

  // The device refused the key. Its event is authoritative over our optimism.
  useEffect(() => {
    if (!rejection) return;
    if (pendingSince && new Date(rejection.ts).getTime() < pendingSince) return;
    setErrorMsg(SETTINGS_ERROR_LABEL[rejection.code] ?? `error ${rejection.code}`);
    setMode('errored');
    setPendingSince(null);
  }, [rejection, pendingSince]);

  const effectiveMode: RowMode =
    mode === 'editing' || mode === 'errored'
      ? mode
      : mode === 'pending' || shadowPending
        ? 'pending'
        : 'idle';
  // What the device has been asked for, whoever asked.
  const pendingValue = mode === 'pending' && retryValue !== null ? retryValue : desiredValue;
  const stalled =
    effectiveMode === 'pending' &&
    pendingSince !== null &&
    Date.now() - pendingSince > PENDING_PATIENCE_MS;

  // Border-left signal — accent during pending, warn after error.
  const railClass =
    effectiveMode === 'pending'
      ? 'border-l-2 border-accent'
      : effectiveMode === 'errored'
        ? 'border-l-2 border-warn'
        : 'border-l-2 border-transparent';

  return (
    <div
      ref={rowRef}
      className={`group flex items-baseline gap-3 px-2 py-1.5 border-b border-border last:border-0 ${railClass}`}
    >
      <div className="flex-1 text-sm text-fg">
        {label}
        {descriptor.description && effectiveMode === 'idle' && (
          <span className="ml-2 text-[10px] text-muted">
            {descriptor.description}
          </span>
        )}
        {errorMsg && effectiveMode === 'errored' && (
          <span className="ml-2 text-[11px] text-warn">
            failed: {errorMsg}
          </span>
        )}
      </div>
      <div className="flex items-center gap-2">
        {effectiveMode === 'idle' && (
          <>
            <span
              className={`text-sm font-mono tabular-nums ${
                isReadOnly ? 'text-muted italic' : 'text-fg'
              }`}
            >
              {displayValue(reportedValue, descriptor)}
            </span>
            {!isReadOnly && (
              <button
                type="button"
                onClick={() => beginEdit()}
                className="opacity-0 group-hover:opacity-100 text-muted hover:text-fg text-xs px-1 transition-opacity"
                aria-label={`Edit ${label}`}
                title={`Edit ${label}`}
              >
                {isWriteOnly ? 'set new' : '✎'}
              </button>
            )}
          </>
        )}
        {effectiveMode === 'editing' && (
          <EditorForType
            descriptor={descriptor}
            initial={retryValue ?? reportedValue}
            onCommit={commit}
            onCancel={cancel}
          />
        )}
        {effectiveMode === 'pending' && (
          <span className="flex items-center gap-1.5 text-sm">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-accent animate-pulse" />
            <span className="font-mono tabular-nums italic text-muted">
              {displayValue(pendingValue, descriptor)}
            </span>
            <span
              className="text-[10px] text-muted uppercase tracking-wider"
              title={
                stalled
                  ? 'Written to the device shadow but not applied yet — the device may be offline.'
                  : 'Written to the device shadow; waiting for the device to report it back.'
              }
            >
              {stalled ? 'not applied yet' : 'waiting for device…'}
            </span>
          </span>
        )}
        {effectiveMode === 'errored' && (
          <>
            <span className="text-sm font-mono tabular-nums text-fg">
              {displayValue(reportedValue, descriptor)}
            </span>
            <button
              type="button"
              onClick={() => beginEdit(retryValue)}
              className="text-xs text-accent hover:underline"
              title={`Retry editing ${label}`}
            >
              retry
            </button>
            <button
              type="button"
              onClick={cancel}
              className="text-xs text-muted hover:text-fg"
              title="Dismiss error"
            >
              dismiss
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// Picks the right editor for the descriptor's declared type. The
// `initial` value is cast at the boundary; the editor itself handles
// out-of-band values defensively (e.g. IntEditor falls back to cancel
// if the input doesn't parse).
function EditorForType({
  descriptor,
  initial,
  onCommit,
  onCancel,
}: {
  descriptor: SettingDescriptor;
  initial: unknown;
  onCommit: (v: unknown) => void;
  onCancel: () => void;
}) {
  if (descriptor.write_only) {
    return (
      <WriteOnlyEditor
        initial=""
        descriptor={descriptor}
        onCommit={onCommit}
        onCancel={onCancel}
      />
    );
  }
  switch (descriptor.type) {
    case 'int':
      return (
        <IntEditor
          initial={typeof initial === 'number' ? initial : 0}
          descriptor={descriptor}
          onCommit={onCommit}
          onCancel={onCancel}
        />
      );
    case 'bool':
      return (
        <BoolEditor
          initial={Boolean(initial)}
          descriptor={descriptor}
          onCommit={onCommit}
          onCancel={onCancel}
        />
      );
    case 'enum':
      return (
        <EnumEditor
          initial={(initial as number | string) ?? descriptor.enum?.[0]?.value ?? 0}
          descriptor={descriptor}
          onCommit={onCommit}
          onCancel={onCancel}
        />
      );
    case 'text':
      return (
        <TextEditor
          initial={typeof initial === 'string' ? initial : ''}
          descriptor={descriptor}
          onCommit={onCommit}
          onCancel={onCancel}
        />
      );
    case 'text[]':
      return (
        <TextListEditor
          initial={Array.isArray(initial) ? (initial as string[]) : []}
          descriptor={descriptor}
          onCommit={onCommit}
          onCancel={onCancel}
        />
      );
    default:
      return null;
  }
}
